import React, { lazy, Suspense } from 'react';

import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { PageNotFound } from 'containers/PageNotFound';
import {
  ADDRESS_INPUT_PATH,
  ADDRESS_UPDATE_PATH,
  APPOINTMENT_PATH,
  APPOINTMENT_UPDATE_PATH,
  BOOKING_PATH,
  CALENDAR_IMPORT_PATH,
  CANCEL_APPOINTMENT_PATH,
  CARE_PLAN_PATH,
  COMMUNICATION_PATH,
  CREDIT_CARD_INPUT_PATH,
  DEV_PATH,
  EDIT_USER_DETAILS_PATH,
  END_SESSION_EARLY_PATH,
  LOGIN_GOOGLE_REDIRECT_PATH,
  LOGIN_PATH,
  MORE_PATH,
  PATIENT_PAYMENT_PATH,
  PATIENT_REFERRAL_PATH,
  PATIENT_REFERRAL_SUCCESS_PATH,
  PAYMENT_SUCCESS_PATH,
  POST_SESSION_PATH,
  RESCHEDULE_PATH,
  RESCHEDULE_SUCCESS_PATH,
  ROOT_REDIRECT_PATH,
  SCHEDULE_PATH,
  USER_DETAILS_PATH,
} from './routePaths';
import { PrivateRoute } from './PrivateRoute';
import { AppContainer } from 'containers/AppContainer/AppContainer';
import { Login } from 'pages/login/Login';
import { LoginGoogleRedirect } from 'pages/login/LoginGoogle/LoginGoogleRedirect';
import { Loader } from 'components/Loader/Loader';

const Schedule = lazy(() =>
  import('pages/schedule/Schedule').then(m => ({ default: m.Schedule })),
);
const Booking = lazy(() =>
  import('pages/booking/Booking').then(m => ({ default: m.Booking })),
);
const MorePage = lazy(() =>
  import('pages/more/MorePage').then(m => ({ default: m.MorePage })),
);
const AppointmentPage = lazy(() =>
  import('pages/appointment/AppointmentPage').then(m => ({
    default: m.AppointmentPage,
  })),
);
const AppointmentCancellationPage = lazy(() =>
  import(
    'pages/appointment/AppointmentCancellationPage/AppointmentCancellationPage'
  ).then(m => ({ default: m.AppointmentCancellationPage })),
);
const PaymentPage = lazy(() =>
  import('pages/payment/PaymentPage').then(m => ({ default: m.PaymentPage })),
);
const EndSessionConfirmation = lazy(() =>
  import('pages/session/EndSessionConfirmation/EndSessionConfirmation').then(
    m => ({ default: m.EndSessionConfirmation }),
  ),
);
const TestPage = lazy(() =>
  import('pages/development/TestPage').then(m => ({ default: m.TestPage })),
);
const PaymentSuccess = lazy(() =>
  import('pages/paymentSuccess/PaymentSuccess').then(m => ({
    default: m.PaymentSuccess,
  })),
);
const AddressSelectorPage = lazy(() =>
  import('pages/addressUpdate/AddressSelectorPage').then(m => ({
    default: m.AddressSelectorPage,
  })),
);
const AddressInputPage = lazy(() =>
  import('pages/addressInput/AddressInputPage').then(m => ({
    default: m.AddressInputPage,
  })),
);
const AppointmentReschedulePage = lazy(() =>
  import(
    'pages/appointment/AppointmentReschedulePage/AppointmentReschedulePage'
  ).then(m => ({ default: m.AppointmentReschedulePage })),
);
const RescheduleSuccessPage = lazy(() =>
  import(
    'pages/appointment/AppointmentReschedulePage/RescheduleSuccessPage'
  ).then(m => ({ default: m.RescheduleSuccessPage })),
);
const PostSessionPage = lazy(() =>
  import('pages/postSession/PostSessionPage').then(m => ({
    default: m.PostSessionPage,
  })),
);
const CardInputPage = lazy(() =>
  import('pages/payment/CardInputPage').then(m => ({
    default: m.CardInputPage,
  })),
);
const UserDetailsPage = lazy(() =>
  import('pages/more/UserDetailsPage').then(m => ({
    default: m.UserDetailsPage,
  })),
);
const EditUserDetailsPage = lazy(() =>
  import('pages/more/EditUserDetailsPage/EditUserDetailsPage').then(m => ({
    default: m.EditUserDetailsPage,
  })),
);
const CarePlanPage = lazy(() =>
  import('pages/carePlan/CarePlanPage').then(m => ({
    default: m.CarePlanPage,
  })),
);
const PatientReferralPage = lazy(() =>
  import('pages/patientReferral/PatientReferralPage').then(m => ({
    default: m.PatientReferralPage,
  })),
);
const PatientReferralSuccessPage = lazy(() =>
  import('pages/patientReferral/PatientReferralSuccessPage').then(m => ({
    default: m.PatientReferralSuccessPage,
  })),
);
const CommunicationRouter = lazy(() =>
  import('pages/communication/CommunicationRouter').then(m => ({
    default: m.CommunicationRouter,
  })),
);
const CalendarImportPage = lazy(() =>
  import('pages/calendarImport/CalendarImportPage').then(m => ({
    default: m.CalendarImportPage,
  })),
);
const AppointmentUpdatePage = lazy(() =>
  import('pages/appointment/AppointmentUpdatePage/AppointmentUpdatePage').then(
    m => ({ default: m.AppointmentUpdatePage }),
  ),
);

const RouteFallback = () => (
  <Loader isLoading loadingLabel="Loading page" minHeight="100vh" />
);

export const Router = () => (
  <BrowserRouter>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path={LOGIN_PATH} element={<Login />} />
        <Route
          path={LOGIN_GOOGLE_REDIRECT_PATH}
          element={<LoginGoogleRedirect />}
        />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <AppContainer>
                <Routes>
                  <Route
                    path={CANCEL_APPOINTMENT_PATH.replace('/', '')}
                    element={<AppointmentCancellationPage />}
                  />
                  <Route
                    path={RESCHEDULE_SUCCESS_PATH.replace('/', '')}
                    element={<RescheduleSuccessPage />}
                  />
                  <Route
                    path={RESCHEDULE_PATH.replace('/', '') + '/*'}
                    element={<AppointmentReschedulePage />}
                  />
                  <Route
                    path={APPOINTMENT_UPDATE_PATH.replace('/', '')}
                    element={<AppointmentUpdatePage />}
                  />
                  <Route
                    path={END_SESSION_EARLY_PATH.replace('/', '')}
                    element={<EndSessionConfirmation />}
                  />
                  <Route
                    path={APPOINTMENT_PATH.replace('/', '')}
                    element={<AppointmentPage />}
                  />
                  <Route
                    path={POST_SESSION_PATH.replace('/', '')}
                    element={<PostSessionPage />}
                  />
                  <Route
                    path={SCHEDULE_PATH.replace('/', '')}
                    element={<Schedule />}
                  />
                  <Route
                    path={BOOKING_PATH.replace('/', '') + '/*'}
                    element={<Booking />}
                  />
                  <Route
                    path={MORE_PATH.replace('/', '')}
                    element={<MorePage />}
                  />
                  <Route
                    path={EDIT_USER_DETAILS_PATH.replace('/', '')}
                    element={<EditUserDetailsPage />}
                  />
                  <Route
                    path={USER_DETAILS_PATH.replace('/', '')}
                    element={<UserDetailsPage />}
                  />
                  <Route
                    path={CREDIT_CARD_INPUT_PATH.replace('/', '')}
                    element={<CardInputPage />}
                  />
                  <Route
                    path={PATIENT_PAYMENT_PATH.replace('/', '') + '/*'}
                    element={<PaymentPage />}
                  />
                  <Route
                    path={PAYMENT_SUCCESS_PATH.replace('/', '')}
                    element={<PaymentSuccess />}
                  />
                  <Route
                    path={ADDRESS_UPDATE_PATH.replace('/', '')}
                    element={<AddressSelectorPage />}
                  />
                  <Route
                    path={ADDRESS_INPUT_PATH.replace('/', '')}
                    element={<AddressInputPage />}
                  />
                  <Route
                    path={CARE_PLAN_PATH.replace('/', '')}
                    element={<CarePlanPage />}
                  />
                  <Route
                    path={PATIENT_REFERRAL_SUCCESS_PATH.replace('/', '')}
                    element={<PatientReferralSuccessPage />}
                  />
                  <Route
                    path={PATIENT_REFERRAL_PATH.replace('/', '')}
                    element={<PatientReferralPage />}
                  />
                  <Route
                    path={COMMUNICATION_PATH.replace('/', '') + '/*'}
                    element={<CommunicationRouter />}
                  />
                  <Route
                    path={CALENDAR_IMPORT_PATH.replace('/', '')}
                    element={<CalendarImportPage />}
                  />
                  <Route
                    path={DEV_PATH.replace('/', '')}
                    element={<TestPage />}
                  />
                  <Route
                    path=""
                    element={<Navigate to={ROOT_REDIRECT_PATH} replace />}
                  />
                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </AppContainer>
            </PrivateRoute>
          }
        />
      </Routes>
    </Suspense>
  </BrowserRouter>
);
